import React from 'react'
import styled from '@emotion/styled'

import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'

import {BellIcon} from './assets/BellIcon'
import {BuildingIcon} from './assets/BuildingIcon'
import {CirclePlusIcon} from './assets/CirclePlusIcon'
import {CircleUserIcon} from './assets/CircleUserIcon'
import {MessagesIcon} from './assets/MessagesIcon'
import {CUSTOM_HEADER_HEIGHT, CUSTOM_HEADER_WIDTH} from './constants'
import {FinnLogo} from './FinnLogo'
import {HeaderTabs} from './HeaderTabs'

const HeaderWrapper = styled.header`
  --custom-header-height: ${CUSTOM_HEADER_HEIGHT}px;
  background-color: ${cssTheme.colors.background.neutral.primary};
  border-bottom: 1px solid ${cssTheme.colors.border.neutral.primary};
  width: 100%;
  position: relative;
  margin: 0 auto;
`

const Nav = styled.nav`
  display: flex;
  /* CUSTOM_HEADER_HEIGHT is the total; subtract the wrapper's 1px bottom border. */
  height: calc(${CUSTOM_HEADER_HEIGHT}px - 1px);
  align-items: center;
  background-color: inherit;
  padding: 0 0 0 ${cssTheme.layout.spacing.s500};
  box-sizing: border-box;
  max-width: ${CUSTOM_HEADER_WIDTH}px;
  margin: 0 auto;
  ${linariaMq.desktopXs} {
    padding: 0 ${cssTheme.layout.spacing.s700};
  }
`

const LogoLink = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  border-bottom: 2px solid transparent;
  text-decoration: none;
  color: inherit;
  margin-right: 0;

  ${linariaMq.desktopXs} {
    margin-right: auto;
  }

  &:hover,
  &:focus {
    border-bottom-color: ${cssTheme.colors.brand.c500};
    text-decoration: none;
    outline: none;
  }

  &:active {
    text-decoration: none;
  }
`

const NavLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border-bottom: 2px solid transparent;
  text-decoration: none;
  color: ${cssTheme.colors.content.neutral.primary};
  font-size: ${cssTheme.fonts.size.xs};
  position: relative;
  min-width: 0;
  flex: 1 1 auto;
  padding: 0;
  margin-left: 0;
  box-sizing: border-box;

  ${linariaMq.desktopXs} {
    flex: none;
    padding: 0 10px;
    margin-left: 10px;
  }

  &:hover,
  &:focus {
    border-bottom-color: ${cssTheme.colors.brand.c500};
    text-decoration: none;
    outline: none;
  }

  &:active {
    text-decoration: none;
  }
`

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  color: ${cssTheme.colors.content.neutral.primary};
`

const LogoText = styled.span`
  display: none;
  margin-left: ${cssTheme.layout.spacing.s300};
  color: ${cssTheme.colors.content.neutral.primary};
  font-size: ${cssTheme.fonts.size.xs};

  ${linariaMq.desktopSm} {
    display: inline-block;
  }
`

const TextWrapper = styled.span`
  display: none;
  margin-left: ${cssTheme.layout.spacing.s300};
  color: ${cssTheme.colors.content.neutral.primary};

  ${linariaMq.desktopXs} {
    display: inline-block;
  }
`

const ForBedrifterNavLink = styled(NavLink)`
  display: none;

  ${linariaMq.desktopXs} {
    display: flex;
  }
`

export const FinnHeader = ({isHomePage = false}: {isHomePage?: boolean}) => {
  return (
    <>
      <HeaderWrapper>
        <Nav aria-label="Topp">
          <LogoLink
            href="https://www.finn.no"
            aria-label="Forside"
            aria-current="page"
            title="Forside"
            data-automation-id="frontpage-link"
            target="_blank"
          >
            <FinnLogo />
            <LogoText>Mulighetenes marked</LogoText>
          </LogoLink>

          <ForBedrifterNavLink
            href="https://www.finn.no/bedrift/velg"
            aria-label="For bedrifter"
            title="For bedrifter"
            data-automation-id="business-center-link"
            rel="nofollow"
            target="_blank"
          >
            <IconWrapper>
              <BuildingIcon />
            </IconWrapper>
            <TextWrapper>For bedrifter</TextWrapper>
          </ForBedrifterNavLink>

          <NavLink
            id="top-bar_notifications-link"
            href="https://www.finn.no/notifications"
            aria-label="Varslinger"
            title="Varslinger"
            data-automation-id="notifications-link"
            rel="nofollow"
            target="_blank"
          >
            <IconWrapper>
              <BellIcon />
            </IconWrapper>
            <TextWrapper>Varslinger</TextWrapper>
          </NavLink>

          <NavLink
            href="https://www.finn.no/create-item/start"
            aria-label="Ny annonse"
            title="Ny annonse"
            data-automation-id="new-ad-link"
            rel="nofollow"
            target="_blank"
          >
            <IconWrapper>
              <CirclePlusIcon />
            </IconWrapper>
            <TextWrapper>Ny annonse</TextWrapper>
          </NavLink>

          <NavLink
            id="top-bar_messages-link"
            href="https://www.finn.no/messages"
            aria-label="Meldinger"
            title="Meldinger"
            data-automation-id="inbox-link"
            rel="nofollow"
            target="_blank"
          >
            <IconWrapper>
              <MessagesIcon />
            </IconWrapper>
            <TextWrapper>Meldinger</TextWrapper>
          </NavLink>

          <NavLink
            href="https://www.finn.no/my-page"
            aria-label="Min FINN"
            title="Min FINN"
            data-automation-id="profile-link"
            rel="nofollow"
            target="_blank"
          >
            <IconWrapper>
              <CircleUserIcon />
            </IconWrapper>
            <TextWrapper>Min FINN</TextWrapper>
          </NavLink>
        </Nav>
      </HeaderWrapper>
      {isHomePage && <HeaderTabs />}
    </>
  )
}
